import { createAsyncThunk } from '@reduxjs/toolkit';

import { getHttpClient } from '../../../http/httpClientManager';

// Mirrors the per-row shape of `GET /api/v1/clients/:id/modules`.
// `moduleCode` is the routing key the frontend uses (e.g. 'live-pl'); `moduleId`
// is the registry-row UUID. Label, group placement and the maturity pill are all
// registry-owned — `moduleNavLabel` falls back to `moduleDisplayName` when null.
// `isActive` false is the preview state: the nav shows the row greyed out and
// the page will not mount (see docs/plan/MODULE_ENTITLEMENT_MODEL.md).
export type Module = {
  id: string;
  clientId: string;
  moduleId: string;
  moduleCode: string;
  moduleDisplayName: string;
  moduleNavLabel: string | null;
  moduleStatus: string;
  moduleStatusBadge: string | null;
  moduleNavGroup: string | null;
  moduleNavOrder: number | null;
  configOverride: Record<string, unknown>;
  isActive: boolean;
  assignedAt: string;
  createdAt: string;
};

type ModulesResponse = {
  data: Module[];
  pagination: { total: number; page: number; perPage: number; totalPages: number };
};

export const fetchModules = createAsyncThunk<Module[], string>(
  'app/fetchModules',
  async (clientId) => {
    // Pagination defaults are fine — module assignments per tenant are bounded
    // (low double digits at most). If a tenant ever exceeds MAX_PER_PAGE, we
    // page through here; not worth doing speculatively.
    const res = await getHttpClient('api').get<ModulesResponse>(
      `/api/v1/clients/${clientId}/modules`,
    );
    // `isActive` is deliberately NOT filtered here — a disabled assignment still
    // renders in the nav (greyed, non-navigable), so the slice has to carry it.
    // Only a deprecated/archived registry row drops out entirely.
    return res.data.data.filter((m) => m.moduleStatus === 'active');
  },
);
